'use client';
/**
 * CHOISIR LA REMPLAÇANTE D'UNE CASE — vue Mois du planning (RQ, 01/11/2026).
 * Sert aussi à AJOUTER quelqu'un sur une case vide (même jour, même règle).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « En plus du bouton × pour supprimer, un bouton pour placer une autre
 *   personne à la place de la personne présente. »
 *
 * La fenêtre ne décide rien : elle classe. Le verdict de chaque personne vient
 * de l'écran (`verdict`, la même règle que le serveur — `refusRemplacement` et
 * les habilitations), et le serveur revérifie tout au clic.
 *
 *  · d'abord celles qui PEUVENT et sont libres de tout autre poste ce jour-là ;
 *  · puis celles qui peuvent mais tiennent déjà un autre poste (permis, dit) ;
 *  · enfin celles qui ne peuvent pas, estompées, AVEC le motif — une personne
 *    absente de la liste ferait chercher pourquoi.
 *
 * La recherche n'est qu'un filtre de lecture : `sansSaisie`, la croix ferme
 * sans alerte.
 */
import React from 'react';
import { Modale, inputStyle } from '@/components/ui';

export interface CandidatRemplacement {
  orgId: string;
  nom: string;
  /** `ok` faux : le motif est affiché, le choix est impossible. */
  ok: boolean;
  motif?: string;
  /** Les autres postes que la personne tient déjà ce jour-là. */
  dejaCeJour?: string[];
}

const clef = (t: string) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function ChoixRemplacant({ titre, sousTitre, entete, candidats, onChoisir, onClose }: {
  titre: string;
  sousTitre?: React.ReactNode;
  /** Au-dessus de la recherche — le choix de la séance quand on AJOUTE sur un poste qui en a plusieurs. */
  entete?: React.ReactNode;
  candidats: CandidatRemplacement[];
  /** Rend vrai si le remplacement est passé : la fenêtre se ferme alors. */
  onChoisir: (orgId: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const [q, setQ] = React.useState('');
  const [envoi, setEnvoi] = React.useState<string | null>(null);
  const rang = (c: CandidatRemplacement) => (!c.ok ? 2 : c.dejaCeJour?.length ? 1 : 0);
  const liste = React.useMemo(() => candidats
    .filter((c) => !q.trim() || clef(c.nom).includes(clef(q.trim())))
    .sort((a, b) => rang(a) - rang(b) || a.nom.localeCompare(b.nom, 'fr')), [candidats, q]);

  return (
    <Modale titre={titre} sousTitre={sousTitre} onClose={onClose} sansSaisie largeur={520}>
      {entete}
      <input type="search" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
        placeholder="Chercher une personne…" aria-label="Chercher une personne"
        style={{ ...inputStyle, width: '100%', marginBottom: 10 }} />
      <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '55vh', overflowY: 'auto' }}>
        {liste.map((c) => (
          <button key={c.orgId} type="button" role="listitem" disabled={!c.ok || !!envoi}
            onClick={async () => {
              setEnvoi(c.orgId);
              try { if (await onChoisir(c.orgId)) onClose(); } finally { setEnvoi(null); }
            }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'start',
              padding: '7px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)',
              background: 'var(--surface-card)', cursor: c.ok && !envoi ? 'pointer' : 'default',
              opacity: c.ok ? 1 : 0.55, font: 'inherit', color: 'inherit',
            }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {c.nom}{envoi === c.orgId ? ' — enregistrement…' : ''}
            </span>
            {!c.ok && c.motif && <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{c.motif}</span>}
            {c.ok && !!c.dejaCeJour?.length && (
              <span style={{ fontSize: 11.5, color: 'var(--amber-600)' }}>Tient déjà {c.dejaCeJour.join(', ')} ce jour-là</span>
            )}
          </button>
        ))}
        {!liste.length && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 4 }}>Personne ne correspond à la recherche.</p>}
      </div>
    </Modale>
  );
}
